export const isNumber = (input: any): boolean => {
  return !isNaN(input) && input !== null && typeof input === 'number';
};

/**
 * Returns either the string parsed as a number if possible, or the string itself.
 */
export const tryParseNumber = (value: any) => (isNaN(+value) || value === '' ? value : +value);

/**
 *
 * Rounds to `n` decimals
 */
export const round = (num: number, n: number = 2): number => Math.round(num * 10 ** n) / 10 ** n;

// const isNumber = (input: any): boolean => typeof input === 'number';

// let tests = [
//   null,
//   undefined,
//   -1, 0, 1, 999999, Infinity,
//   'a', '', '1', '-1', '0'
// ]

// for (let test of tests) {
//   console.log(`${JSON.stringify(test)} ==> ${isNumber(test)}`);
// }
// null ==> false
// undefined ==> false
// -1 ==> true
// 0 ==> true
// 1 ==> true
// 999999 ==> true
// null ==> true
// "a" ==> false
// "" ==> false
// "1" ==> false
// "-1" ==> false
// "0" ==> false

/**
 * Given an object with a property, it tests if the property is a number and returns the round of that
 * Otherwise returns the value provided by defaultValue, or null
 * @param obj The object that contains the property to test
 * @param prop The property that will be tested
 * @param decimalsToRound The number of decimals to round to
 * @param defaultValue The default value provided if it cannot complete the transaction
 */
export const roundOrDefault = (obj: any, prop: string, decimalsToRound: number, defaultValue = null) => {
  if (!obj) {
    return defaultValue;
  }
  if (obj && !obj.hasOwnProperty(prop)) {
    return defaultValue;
  }
  if (obj && obj.hasOwnProperty(prop)) {
    if (typeof obj[prop] !== 'number') {
      return defaultValue;
    } else {
      return round(obj[prop] as number, decimalsToRound);
    }
  }
};

/**
 * Calculate the weighted average for a serie of values and frequencies
 * @param series An array of pairs where the first value is the actual value and the second the weighing factor or frequency
 * @return 0 if the total of frequencies is 0, otherwise the calculation of the weighted average
 */
export const weightedAverage = (series: [number, number][]) => {
  let sumOfWeights = 0;
  let sumOfFactors = 0;
  for (const item of series) {
    if (typeof item[0] === 'number' && typeof item[1] === 'number') {
      sumOfWeights += item[1];
      sumOfFactors += item[0] * item[1];
    }
  }
  return sumOfWeights === 0 ? 0 : sumOfFactors / sumOfWeights;
};

/**
 * Allows to detect for falsy values except zero
 */
export const exists = (value: number) => {
  return value || value === 0;
};

/**
 * Returns a text representation of number using the highest available multiplier.
 * @example numberToString(600528, 1) => '600.5K'
 */
export const numberToString = (input: number, decimals = 2): string => {
  // https://en.wikipedia.org/wiki/Names_of_large_numbers

  // +-------+---------------+----------------------+--------------------------+--------+-----------+
  // | Value | Short-Scale   | Long-Scale           | Traditional-European     | Symbol | SI-Prefix |
  // |       | (American)    | (European)           |                          |        |           |
  // +=======+===============+======================+==========================+========+===========+
  // | 10^3  | Thousand      | Thousand             | Thousand                 |    K   | Kilo-     |
  // | 10^6  | Million       | Million              | Million                  |    M   | Mega-     |
  // | 10^9  | Billion       | Thousand million     | Milliard                 |    G   | Giga-     |
  // | 10^12 | Trillion      | Billion              | Billion                  |    T   | Tera-     |
  // | 10^15 | Quadrillion   | Thousand billion     | Billiard                 |    P   | Peta-     |
  // | 10^18 | Quintillion   | Trillion             | Trillion                 |    E   | Exa-      |
  // | 10^21 | Sextillion    | Thousand trillion    | Trilliard                |    Z   | Zetta-    |
  // | 10^24 | Septillion    | Quadrillion          | Quadrillion              |    Y   | Yotta-    |
  // +-------+---------------+----------------------+--------------------------+--------+-----------+

  // const suffixes = ['K', 'M', 'G', 'T', 'P', 'E','Z','Y']; // Long Numeric Scale (European)
  const suffixes = ['K', 'M', 'B', 'T', 'P', 'E', 'Z', 'Y']; // Short Numeric Scale (American)

  const isNegative = input < 0;

  if (Number.isNaN(input)) {
    return null;
  }
  input = Math.abs(input);

  if (input < 1000) {
    return `${round(input, decimals)}`;
  }

  const exp = Math.floor(Math.log(input) / Math.log(1000));

  const formattedInput = round(input / Math.pow(1000, exp), decimals) + suffixes[exp - 1];
  return isNegative ? `(${formattedInput})` : formattedInput;
};

export const sumProduct = (ar1, ar2) => {
  if (ar1.length !== ar2.length) {
    throw new RangeError();
  }

  let sum = 0;

  for (let i = 0; i < ar1.length; i++) {
    sum += ar1[i] * ar2[i];
  }

  return sum;
};
